import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function ClientVehicle() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [form, setForm] = useState({ brand: '', model: '', plate: '', vehicle_type: '' });
  const [classifiedType, setClassifiedType] = useState(null);
  const [needsManualSelection, setNeedsManualSelection] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    fetchBrands();
  }, [user, navigate]);

  const fetchBrands = async () => {
    try {
      // Tentar nova API primeiro
      const res = await axios.get(`${API}/vehicles/v2/brands?limit=100`);
      setBrands(res.data.brands);
    } catch (error) {
      // Fallback para API antiga
      try {
        const res = await axios.get(`${API}/vehicles/brands`);
        setBrands(res.data.brands);
      } catch (err) {
        toast.error('Erro ao carregar marcas');
      }
    }
  };

  const handleBrandChange = async (brand) => {
    setForm({ ...form, brand, model: '', vehicle_type: '' });
    setModels([]);
    setClassifiedType(null);
    setNeedsManualSelection(false);

    try {
      // Tentar nova API primeiro
      const res = await axios.get(`${API}/vehicles/v2/models?brand=${encodeURIComponent(brand)}`);
      // Converter para formato compatível
      setModels(res.data.models.map(m => m.modelo));
    } catch (error) {
      // Fallback para API antiga
      try {
        const res = await axios.get(`${API}/vehicles/models/${encodeURIComponent(brand)}`);
        setModels(res.data.models);
      } catch (err) {
        toast.error('Erro ao carregar modelos');
      }
    }
  };

  const handleModelChange = async (model) => {
    setForm({ ...form, model, vehicle_type: '' });
    setClassifiedType(null);
    setNeedsManualSelection(false);

    if (!form.brand || !model) return;

    try {
      // Tentar nova API primeiro
      const res = await axios.get(`${API}/vehicles/v2/classify?brand=${encodeURIComponent(form.brand)}&model=${encodeURIComponent(model)}`);

      if (res.data.found) {
        setClassifiedType(res.data);
        setForm(prev => ({ ...prev, vehicle_type: res.data.vehicle_type }));
        toast.success(`Veículo identificado como ${res.data.vehicle_type_display}!`);
      } else {
        setNeedsManualSelection(true);
        toast.warning('Modelo não encontrado. Selecione o tipo manualmente.');
      }
    } catch (error) {
      // Fallback para API antiga
      try {
        const res = await axios.post(`${API}/vehicles/classify`, null, {
          params: { brand: form.brand, model }
        });

        if (res.data.needs_manual_selection) {
          setNeedsManualSelection(true);
          toast.warning('Modelo não encontrado. Selecione o tipo manualmente.');
        } else {
          setClassifiedType(res.data);
          setForm(prev => ({ ...prev, vehicle_type: res.data.vehicle_type }));
          toast.success(`Veículo identificado como ${res.data.vehicle_type_display}!`);
        }
      } catch (err) {
        console.error(err);
        setNeedsManualSelection(true);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.brand || !form.model || !form.plate) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    if (needsManualSelection && !form.vehicle_type) {
      toast.error('Por favor, selecione o tipo do veículo');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/client/vehicle`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const vehicle = res.data.vehicle;
      const vehicleTypeDisplay = res.data.vehicle_type_display;

      toast.success(`Veículo cadastrado como ${vehicleTypeDisplay}!`);

      // Salvar apenas dados do veículo (planos serão buscados da API na tela de planos)
      sessionStorage.setItem('identified_vehicle', JSON.stringify(vehicle));
      sessionStorage.setItem('vehicle_type_display', vehicleTypeDisplay);

      navigate('/client/plans');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar veículo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <h1 className="text-2xl font-bold mb-2">Cadastrar Veículo</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Selecione a marca e modelo para identificação automática
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="brand">Marca do Veículo *</Label>
            <Select value={form.brand} onValueChange={handleBrandChange}>
              <SelectTrigger id="brand">
                <SelectValue placeholder="Selecione a marca..." />
              </SelectTrigger>
              <SelectContent>
                {brands.map((brand) => (
                  <SelectItem key={brand} value={brand}>
                    {brand}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="model">Modelo do Veículo *</Label>
            <Select 
              value={form.model} 
              onValueChange={handleModelChange}
              disabled={!form.brand}
            >
              <SelectTrigger id="model">
                <SelectValue placeholder={form.brand ? "Selecione o modelo..." : "Primeiro selecione a marca"} />
              </SelectTrigger>
              <SelectContent>
                {models.map((model) => (
                  <SelectItem key={model} value={model}>
                    {model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {classifiedType && !needsManualSelection && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-sm text-green-800">
                <strong>Veículo identificado:</strong> {classifiedType.vehicle_type_display}
                <br />
                <span className="text-xs">Os planos serão ajustados automaticamente para este tipo.</span>
              </AlertDescription>
            </Alert>
          )}

          {needsManualSelection && (
            <>
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-sm text-amber-800">
                  <strong>Modelo não encontrado no catálogo.</strong>
                  <br />
                  Por favor, selecione manualmente o tipo do seu veículo.
                </AlertDescription>
              </Alert>

              <div>
                <Label htmlFor="vehicle_type">Tipo do Veículo *</Label>
                <Select value={form.vehicle_type} onValueChange={(value) => setForm({ ...form, vehicle_type: value })}>
                  <SelectTrigger id="vehicle_type">
                    <SelectValue placeholder="Selecione o tipo..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">
                      <div className="py-1">
                        <p className="font-semibold">Carro Pequeno</p>
                        <p className="text-xs text-muted-foreground">Hatch, Sedan compacto</p>
                      </div>
                    </SelectItem>
                    <SelectItem value="suv">
                      <div className="py-1">
                        <p className="font-semibold">SUV</p>
                        <p className="text-xs text-muted-foreground">Utilitário esportivo, Crossover</p>
                      </div>
                    </SelectItem>
                    <SelectItem value="pickup">
                      <div className="py-1">
                        <p className="font-semibold">Caminhonete</p>
                        <p className="text-xs text-muted-foreground">Picape, Pickup</p>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="plate">Placa do Veículo *</Label>
            <Input
              id="plate"
              placeholder="ABC-1234"
              value={form.plate}
              onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Apenas para identificação do veículo
            </p>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !form.brand || !form.model || !form.plate || (needsManualSelection && !form.vehicle_type)}
          >
            {loading ? 'Cadastrando...' : 'Cadastrar e Ver Planos'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
